import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Button, LoadingContent, Select } from '../../../components'
import PdcaLayout from '../base/pdcaLayout'
import PdcaPageHeader from '../components/pdcapageheader/pdcapageheader'
import PdcaEmptyState from '../components/pdcaemptystate/pdcaemptystate'
import PdcaPlanoCard from '../components/pdcaplanocard/pdcaplanocard'
import Detalheplano from '../detalheplano/detalheplano'
import Agiract from '../agiract/agiract'
import { setPeriodoPdca } from '../base/redux/pdcaBaseActions'
import { fetchAct, clearAct } from './redux/actActions'
import './act.scss'

const obterTipoPlano = (tipoPlano, nls) => {
  const mapa = {
    diario: nls.planTypeDaily,
    semanal: nls.planTypeWeekly,
    mensal: nls.planTypeMonthly
  }

  return mapa[tipoPlano] || tipoPlano
}

const obterEscopo = (item, nls) => {
  if (item.lojaNome) return `${nls.scopeStore}: ${item.lojaNome}`
  if (item.produtoNome) return `${nls.scopeProduct}: ${item.produtoNome}`
  return nls.scopeCompany
}

const obterStatus = (status, nls) => {
  const mapa = {
    aguardando_decisao: nls.aguardando_decisao,
    decidido: nls.decidido,
    atrasado: nls.atrasado,
    atraso_critico: nls.atraso_critico
  }

  return mapa[status] || status
}

const obterResultado = (resultado, nls) => {
  const mapa = {
    resolvido: nls.resultado_resolvido,
    nao_resolvido: nls.resultado_nao_resolvido,
    rejeitado: nls.resultado_rejeitado,
    parcial: nls.resultado_parcial
  }

  return mapa[resultado] || resultado
}

const obterVariantStatus = status => {
  if (status === 'atrasado' || status === 'atraso_critico') return 'danger'
  if (status === 'aguardando_decisao') return 'warning'
  return 'neutral'
}

const formatarDataInput = data => {
  const ano = data.getFullYear()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${ano}-${mes}-${dia}`
}

const obterHojeInput = () => formatarDataInput(new Date())

const adicionarDiasInput = (dataTexto, dias) => {
  if (!dataTexto) return ''

  const [ano, mes, dia] = dataTexto.split('-').map(Number)
  const data = new Date(ano, mes - 1, dia)
  data.setDate(data.getDate() + dias)

  return formatarDataInput(data)
}

const diferencaDiasInput = (dataInicialTexto, dataFinalTexto) => {
  if (!dataInicialTexto || !dataFinalTexto) return 0

  const [anoInicial, mesInicial, diaInicial] = dataInicialTexto.split('-').map(Number)
  const [anoFinal, mesFinal, diaFinal] = dataFinalTexto.split('-').map(Number)

  const dataInicial = new Date(anoInicial, mesInicial - 1, diaInicial)
  const dataFinal = new Date(anoFinal, mesFinal - 1, diaFinal)

  const diffMs = dataFinal.getTime() - dataInicial.getTime()
  return Math.floor(diffMs / 86400000)
}

const formatarMelhoriaPercentual = (valor, nls) => {
  if (!valor || valor === '') {
    return nls.naodisponivel
  }

  const texto = String(valor).trim()

  if (texto.endsWith('%')) {
    return texto
  }

  return `${texto}%`
}

export default () => {
  const dispatch = useDispatch()
  const { idProjetos } = useParams()

  const { sistema, projeto } = useSelector(state => state.layoutState)
  const nls = require(`./nls/${sistema.idioma}.json`)

  const {
    projetoId,
    empresaPdcaExiste,
    empresaPdcaId,
    contextoCarregado,
    dataInicial: dataInicialGlobal,
    dataFinal: dataFinalGlobal
  } = useSelector(state => state.pdcaState.pdcaBaseState)

  const {
    itens = [],
    total = 0,
    loading = false,
    error = null
  } = useSelector(state => state.pdcaState.pdcaActState || {})

  const statusOptions = useMemo(() => ([
    { label: nls.todos, value: 'todos' },
    { label: nls.aguardando_decisao, value: 'aguardando_decisao' },
    { label: nls.decidido, value: 'decidido' },
    { label: nls.atrasado, value: 'atrasado' },
    { label: nls.atraso_critico, value: 'atraso_critico' }
  ]), [
    nls.todos,
    nls.aguardando_decisao,
    nls.decidido,
    nls.atrasado,
    nls.atraso_critico
  ])

  const [dataInicial, setDataInicial] = useState(dataInicialGlobal || '')
  const [dataFinal, setDataFinal] = useState(dataFinalGlobal || '')
  const [erroFiltro, setErroFiltro] = useState(null)
  const [statusSelecionado, setStatusSelecionado] = useState(statusOptions[0] || {})
  const [detalheOpen, setDetalheOpen] = useState(false)
  const [planoIdSelecionado, setPlanoIdSelecionado] = useState(null)
  const [agirActOpen, setAgirActOpen] = useState(false)
  const [planoIdAgirAct, setPlanoIdAgirAct] = useState(null)

  const contextoValido =
    contextoCarregado &&
    String(projetoId) === String(idProjetos)

  const podeGerirProjeto = projeto?.permissaoPdcaGerirProjeto === true

  useEffect(() => {
    setDataInicial(dataInicialGlobal || '')
    setDataFinal(dataFinalGlobal || '')
  }, [dataInicialGlobal, dataFinalGlobal])

  useEffect(() => {
    setStatusSelecionado(statusOptions[0] || {})
  }, [statusOptions])

  useEffect(() => {
    return () => {
      dispatch(clearAct())
    }
  }, [dispatch])

  useEffect(() => {
    if (!idProjetos || !contextoValido || !empresaPdcaExiste) return
    if (!dataInicialGlobal || !dataFinalGlobal) return

    dispatch(fetchAct({
      empresaId: empresaPdcaId,
      projetoId: idProjetos,
      idiomaId: sistema.idiomaIdValue,
      dataInicial: dataInicialGlobal,
      dataFinal: dataFinalGlobal
    }))
  }, [
    dispatch,
    idProjetos,
    contextoValido,
    empresaPdcaExiste,
    empresaPdcaId,
    sistema.idiomaIdValue,
    dataInicialGlobal,
    dataFinalGlobal
  ])

  const contextoInconsistente = contextoCarregado && !empresaPdcaExiste
  const subtitle = `${total} ${total === 1 ? nls.summaryItemSingular : nls.summaryItemPlural}`

  const hojeInput = obterHojeInput()

  const dataInicialMax = dataFinal || hojeInput
  const dataFinalMin = dataInicial || ''

  const dataFinalMaxCalculada = dataInicial
    ? adicionarDiasInput(dataInicial, 59)
    : hojeInput

  const dataFinalMax =
    dataFinalMaxCalculada && dataFinalMaxCalculada < hojeInput
      ? dataFinalMaxCalculada
      : hojeInput

  const handlePesquisar = () => {
    if (!dataInicial || !dataFinal) {
      setErroFiltro(nls.filterErrorRequiredDates)
      return
    }

    if (dataFinal < dataInicial) {
      setErroFiltro(nls.filterErrorEndBeforeStart)
      return
    }

    if (diferencaDiasInput(dataInicial, dataFinal) > 59) {
      setErroFiltro(nls.filterErrorMaxRange)
      return
    }

    if (dataInicial > hojeInput || dataFinal > hojeInput) {
      setErroFiltro(nls.filterErrorFutureDate)
      return
    }

    setErroFiltro(null)

    dispatch(setPeriodoPdca({
      dataInicial,
      dataFinal
    }))
  }

  const handleSelecionarStatus = e => {
    setStatusSelecionado(e?.value || statusOptions[0] || {})
  }

  const handleDetalhes = planoId => {
    if (!planoId) {
      return
    }

    setPlanoIdSelecionado(planoId)
    setDetalheOpen(true)
  }

  const handleAgirAct = planoId => {
    if (!planoId) {
      return
    }

    setPlanoIdAgirAct(planoId)
    setAgirActOpen(true)
  }

  const handleAgirActSuccess = () => {
    if (!idProjetos || !empresaPdcaExiste || !dataInicialGlobal || !dataFinalGlobal) {
      return
    }

    dispatch(fetchAct({
      empresaId: empresaPdcaId,
      projetoId: idProjetos,
      idiomaId: sistema.idiomaIdValue,
      dataInicial: dataInicialGlobal,
      dataFinal: dataFinalGlobal
    }))
  }

  const itensFiltrados = Array.isArray(itens)
    ? itens.filter(item => {
        if (statusSelecionado?.value === 'todos') return true
        return item.status === statusSelecionado?.value
      })
    : []

  

  return (
    <PdcaLayout>
      <div className='box-pdca-act'>
        <PdcaPageHeader
          title={nls.pageTitle}
          subtitle={subtitle}
        />

        <div className='box-pdca-act-search-box'>
          <div className='box-pdca-act-filters'>
            <div className='box-pdca-act-filter-field box-pdca-act-filter-field-date'>
              <label>{nls.filterStartDate}</label>
              <input
                type='date'
                value={dataInicial}
                max={dataInicialMax}
                onChange={e => {
                  const novoValor = e.target.value
                  setDataInicial(novoValor)

                  if (dataFinal) {
                    const dataFinalInvalida =
                      dataFinal < novoValor ||
                      diferencaDiasInput(novoValor, dataFinal) > 59

                    if (dataFinalInvalida) {
                      setDataFinal('')
                    }
                  }
                }}
              />
            </div>

            <div className='box-pdca-act-filter-field box-pdca-act-filter-field-date'>
              <label>{nls.filterEndDate}</label>
              <input
                type='date'
                value={dataFinal}
                min={dataFinalMin || undefined}
                max={dataFinalMax}
                onChange={e => setDataFinal(e.target.value)}
              />
            </div>

            <div className='box-pdca-act-filter-action'>
              <Button color='primary' onClick={handlePesquisar}>
                {nls.filterSearch}
              </Button>
            </div>

            <div className='box-pdca-act-status-filter'>
              <div className='box-pdca-act-status-select'>
                <Select
                  name='statusAct'
                  label={nls.filterStatus}
                  options={statusOptions}
                  selected={statusSelecionado || {}}
                  action={handleSelecionarStatus}
                  selectedItem={false}
                  optionLabel='label'
                  optionValue='value'
                />
              </div>
            </div>
          </div>
        </div>

        {erroFiltro && (
          <div className='box-pdca-act-inline-error'>
            {erroFiltro}
          </div>
        )}

        {contextoInconsistente && (
          <div className='box-pdca-act-inline-error'>
            {nls.contextError}
          </div>
        )}

        {!contextoInconsistente && (
          <LoadingContent loading={loading}>
            {error && (
              <div className='box-pdca-act-inline-error'>
                {nls.loadError}
              </div>
            )}

            {!error && (
              <>
                {itensFiltrados.length ? (
                  <div className='box-pdca-act-list'>
                    {itensFiltrados.map(item => {
                      const metaItems = [
                        { label: nls.metaStatus, value: obterStatus(item.status, nls) },
                        { label: nls.metaEndDate, value: item.dataFinal },
                        {
                          label: nls.metaImprovementPercentage,
                          value: formatarMelhoriaPercentual(item.melhoriaPercentual, nls)
                        },
                        { label: nls.metaResult, value: obterResultado(item.resultado, nls) }
                      ]

                      const actions = [
                        { key: 'details', label: nls.actionDetails, primary: false }
                      ]

                      if (item.status !== 'atraso_critico' && podeGerirProjeto) {
                        actions.unshift({
                          key: 'act',
                          label: nls.actionAct,
                          primary: true
                        })
                      }

                      if (item.status === 'atrasado') {
                        metaItems.push({
                          label: nls.metaDaysToCriticalDelay,
                          value: item.diasParaAtrasoCritico
                        })
                      }

                      return (
                        <PdcaPlanoCard
                          key={`act-${item.id}`}
                          item={item}
                          nls={nls}
                          titlePrefix={nls.planNumber}
                          typeLabel={obterTipoPlano(item.tipoPlano, nls)}
                          scopeText={obterEscopo(item, nls)}
                          badges={[
                            {
                              key: `status-${item.id}`,
                              variant: obterVariantStatus(item.status),
                              label: obterStatus(item.status, nls)
                            },
                            {
                              key: `tipo-${item.id}`,
                              variant: 'neutral',
                              label: obterTipoPlano(item.tipoPlano, nls)
                            }
                          ]}
                          metaItems={metaItems}
                          actions={actions}
                          onDetalhes={handleDetalhes}
                          onAgirAct={handleAgirAct}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <PdcaEmptyState
                    title={
                      itens.length
                        ? nls.emptyStatusFilter
                        : nls.emptyState
                    }
                  />
                )}
              </>
            )}
          </LoadingContent>
        )}

        <Detalheplano
          open={detalheOpen}
          close={() => setDetalheOpen(false)}
          planoId={planoIdSelecionado}
        />

        <Agiract
          open={agirActOpen}
          close={() => setAgirActOpen(false)}
          planoId={planoIdAgirAct}
          onSuccess={handleAgirActSuccess}
        />
      </div>
    </PdcaLayout>
  )
}